/**
 * The row of numbers under a grid.
 *
 * One cell per column, aligned with it, holding whatever that column has been
 * asked to work out -- and a menu on each to change it. At the bottom of the
 * scroll container and horizontally in step with the columns, which is the
 * header's arrangement upside down and needs no synchronisation for the same
 * reason: it is in the same scroll container as everything it lines up with.
 *
 * Held down there by two rules, because one is not enough. `mt-auto` takes the
 * free space of the column the surface lays it out in, which puts it at the
 * bottom of the window when the table is shorter than the window; `sticky`
 * holds it there once the table is taller and the rows scroll under it. Sticky
 * alone strands it under the last row of a five-row table, because sticky only
 * ever lifts an element towards the edge it is pinned to and never pushes one
 * down to meet it.
 *
 * A cell with no aggregation is not empty of controls -- it is how one is
 * chosen. That is why every column has a cell rather than only the ones showing
 * something: a footer with gaps in it reads as a footer that has lost track of
 * some columns.
 */
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field } from '@/api/types'
import { fieldKey } from '@/api/types'
import { cn } from '@/shared/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import {
  aggregationText,
  aggregationValue,
  isAggregationTypeName,
  viewAggregationsForField,
  type AggregationTypeName,
} from '../aggregations'
import type { ViewAggregations } from '../aggregations/queries'
import type { FieldContext } from '../fields/behavior/behavior'
import type { GridColumn, GridFieldOptions } from './columns'

/** How tall the footer is, in pixels. */
export const FOOTER_HEIGHT = 32

export function AggregationFooter({
  columns,
  offsets,
  gutterLeft,
  gutter,
  options,
  aggregations,
  context,
  onChoose,
}: {
  columns: readonly GridColumn[]
  offsets: readonly number[]
  /** How far in the gutter starts: the width of the group columns. */
  gutterLeft: number
  /** How wide the row-identifier gutter is. */
  gutter: number
  options: Record<string, GridFieldOptions>
  /** The answers, or undefined while nothing has been asked or answered. */
  aggregations: ViewAggregations | undefined
  context: FieldContext | undefined
  /**
   * `null` turns the column's aggregation off.
   *
   * Absent on a publicly shared view, where the numbers are the owner's choice
   * and a reader has nothing to change -- so the cells are text rather than
   * controls that would have to refuse.
   */
  onChoose?: (field: Field, type: AggregationTypeName | null) => void
}) {
  const { t } = useTranslation()

  return (
    <div
      // Not a `row`: it is a set of controls that happens to line up with the
      // columns, and announcing it as a row of the grid would put it in the row
      // count and make every cell in it a gridcell somebody can arrow into.
      role="group"
      aria-label={t('view.aggregations')}
      data-testid="aggregations"
      className="group/aggregations sticky bottom-0 z-20 mt-auto flex shrink-0 border-t bg-background"
      style={{ height: FOOTER_HEIGHT, paddingLeft: gutterLeft }}
    >
      <div
        className="sticky z-10 shrink-0 border-r bg-background"
        style={{ width: gutter, left: gutterLeft }}
      />
      {columns.map((column, position) => (
        <AggregationCell
          key={column.field.id}
          field={column.field}
          width={column.width}
          {...(column.frozen ? { left: offsets[position] } : {})}
          chosen={chosenAggregation(options[String(column.field.id)])}
          aggregations={aggregations}
          context={context}
          {...(onChoose === undefined ? {} : { onChoose })}
        />
      ))}
    </div>
  )
}

/**
 * Which aggregation a column shows, if the stored one is one we know.
 *
 * An unrecognised name reads as none rather than throwing: the options are the
 * server's, and a view configured by an older or newer build of this
 * application is not a reason for a grid to stop rendering.
 */
function chosenAggregation(
  options: GridFieldOptions | undefined,
): AggregationTypeName | null {
  const stored = options?.aggregation_type
  return isAggregationTypeName(stored) ? stored : null
}

function AggregationCell({
  field,
  width,
  left,
  chosen,
  aggregations,
  context,
  onChoose,
}: {
  field: Field
  width: number
  left?: number
  chosen: AggregationTypeName | null
  aggregations: ViewAggregations | undefined
  context: FieldContext | undefined
  onChoose?: (field: Field, type: AggregationTypeName | null) => void
}) {
  const { t } = useTranslation()
  const offered = viewAggregationsForField(field)

  // A column nothing can be worked out over -- a uuid, a password -- gets a
  // cell so the footer stays aligned, and no control, because there is nothing
  // to choose from. A reader of a shared view gets the same treatment for every
  // column: the choice is not theirs.
  const value =
    chosen === null || aggregations === undefined
      ? null
      : aggregationValue(
          chosen,
          aggregations.values[fieldKey(field.id)],
          aggregations.rowCount,
        )

  if (offered.length === 0 || onChoose === undefined) {
    return (
      <div
        className={cn(
          'flex shrink-0 items-center justify-end gap-1 border-r px-2 text-xs text-muted-foreground',
          left !== undefined && 'sticky z-10 bg-background',
        )}
        style={{ width, ...(left === undefined ? {} : { left }) }}
      >
        {chosen !== null && offered.length > 0 && (
          <>
            <span className="truncate">{t(`aggregation.${chosen}`)}</span>
            <span className="font-medium text-foreground">
              {aggregationText(chosen, field, value, context)}
            </span>
          </>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex shrink-0 border-r',
        left !== undefined && 'sticky z-10 bg-background',
      )}
      style={{ width, ...(left === undefined ? {} : { left }) }}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            // Out of the tab order, like the header menus and the row openers:
            // one per column would be forty tab stops under a widget the plan
            // deliberately made into one.
            tabIndex={-1}
            aria-label={t('view.aggregationFor', { field: field.name })}
            // The whole cell, rather than what is drawn in it: a column with
            // no aggregation shows one chevron, and a control the width of a
            // chevron is a control nobody hits.
            className="flex h-full w-full min-w-0 items-center justify-end gap-1 px-2 text-xs text-muted-foreground hover:bg-muted"
          >
            {chosen === null ? (
              // Shown while the pointer is anywhere in the footer rather than
              // over this one cell: a row of blank cells is a row nobody knows
              // is a row of controls.
              <ChevronDown className="size-3 opacity-0 group-hover/aggregations:opacity-100" />
            ) : (
              <>
                <span className="truncate">{t(`aggregation.${chosen}`)}</span>
                <span className="font-medium text-foreground">
                  {aggregationText(chosen, field, value, context)}
                </span>
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              onChoose(field, null)
            }}
          >
            {t('view.aggregationNone')}
          </DropdownMenuItem>
          {offered.map((type) => (
            <DropdownMenuItem
              key={type}
              onSelect={() => {
                onChoose(field, type)
              }}
            >
              {t(`aggregation.${type}`)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
